import { useCallback, useEffect, useRef, useState } from 'react';

import { Box, ButtonBase, Paper, Zoom } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ClearIcon from '@material-ui/icons/Clear';
import SendIcon from '@material-ui/icons/Send';
import clsx from 'clsx';
import { useSnackbar } from 'notistack';

import { ChatInput, ChatInputRef } from 'components/ChatInput';
import { MentionItem } from 'components/ChatInput/mentionSuggestion';
import EmojiButton from 'components/EmojiButton';
import { useAppSelector } from 'store';
import { atendimentosSelector } from 'store/reducers/atendimentos';
import { TipoCanalEnum } from 'store/reducers/canais';
import { ContatoMencionado } from 'types';
import { whatsappToHtml } from 'utils/whatsappToHtml';

const useStyles = makeStyles(({ palette, spacing }) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    padding: '4px',
    color: palette.text.secondary,
    '& > *': {
      margin: spacing(0, 1),
    },
  },
  rootDisabled: {
    justifyContent: 'end',
    height: '56px',
  },
  inputContainer: {
    flexGrow: 1,
    display: 'flex',
    alignItems: 'center',
    padding: spacing(1, 2),
    borderRadius: spacing(3),
    backgroundColor: palette.background.default,
  },
}));

export declare interface InputFormCaptionProps {
  initialValue?: string;
  initialMentions?: ContatoMencionado[];
  maxChars?: number;
  onInputChange?: (
    value: string,
    contatosMencionados?: ContatoMencionado[],
  ) => void;
  onSendInputText?: (
    value: string,
    callback: (isSuccess: boolean) => void,
  ) => void;
  disabledInput?: boolean;
  onResetCaption?: () => void;
  fetchUsers?: (query: string) => Promise<MentionItem[]>;
}

export default function InputFormCaption(props: InputFormCaptionProps) {
  const {
    maxChars = 6000,
    onInputChange,
    onSendInputText,
    initialValue,
    initialMentions,
    disabledInput,
    onResetCaption,
    fetchUsers,
  } = props;
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const { atendimentoSelecionado } = useAppSelector(atendimentosSelector);

  const tipoCanal = atendimentoSelecionado?.canal?.tipoCanal;

  const isInstagramOrMessengerChannel =
    tipoCanal === TipoCanalEnum.INSTAGRAM ||
    tipoCanal === TipoCanalEnum.MESSENGER;

  const [inputValue, setInputValue] = useState(initialValue || '');
  const chatInputRef = useRef<ChatInputRef>(null);
  const [loading, setLoading] = useState(false);

  const getInitialValue = useCallback(() => {
    if (!initialValue) return '';
    let html = whatsappToHtml(initialValue);

    if (initialMentions?.length) {
      initialMentions.forEach(contato => {
        const alias = `@${contato.contatoNome || contato.waPhoneNumberId}`;
        const regex = new RegExp(`@${contato.waPhoneNumberId}`, 'g');
        const span = `<span data-type="mention" data-id="${
          contato.contatoId
        }" data-label="${
          contato.contatoNome || contato.waPhoneNumberId
        }" data-wa-phone-number-id="${contato.waPhoneNumberId}" data-contact="${
          contato.waPhoneNumberId
        }">${alias}</span>`;
        html = html.replace(regex, span);
      });
    }
    return html;
  }, [initialValue, initialMentions]);

  const handleSendInputText = (text?: unknown) => {
    const value = typeof text === 'string' ? text.trim() : inputValue.trim();

    if (value.length > maxChars) {
      enqueueSnackbar(
        `Você não pode enviar mais de ${maxChars} caracteres de uma vez.`,
      );
      return;
    }

    if (onSendInputText) {
      setLoading(true);

      onSendInputText(value, isSuccess => {
        setLoading(false);
        if (isSuccess) {
          setInputValue('');
          chatInputRef.current?.clear();
        }
      });
    }
  };

  const handleSelectEmoji = (emoji: string) => {
    chatInputRef.current?.insertContent(emoji);
  };

  useEffect(() => {
    setInputValue(initialValue || '');
  }, [initialValue]);

  return (
    <Paper
      className={clsx(classes.root, {
        [classes.rootDisabled]: disabledInput || isInstagramOrMessengerChannel,
      })}
      elevation={0}
    >
      {!disabledInput && !isInstagramOrMessengerChannel && (
        <EmojiButton onSelect={handleSelectEmoji} />
      )}

      {!disabledInput && !isInstagramOrMessengerChannel && (
        <Box className={classes.inputContainer}>
          <ChatInput
            autoFocus
            id="input-send-message"
            ref={chatInputRef}
            fetchUsers={fetchUsers}
            disableMentions={!fetchUsers}
            onSend={handleSendInputText}
            onInputChange={(text, _hasContent, mentions) => {
              setInputValue(text);
              if (onInputChange) onInputChange(text, mentions);
            }}
            placeholder="Adicionar legenda"
            disabled={loading}
            initialValue={getInitialValue()}
            allowEmptySend
          />

          <Zoom in={inputValue.length > 0}>
            <ButtonBase
              disabled={loading}
              onClick={() => {
                setInputValue('');
                chatInputRef.current?.clear();
                onResetCaption?.();
              }}
              disableRipple
            >
              <ClearIcon />
            </ButtonBase>
          </Zoom>
        </Box>
      )}

      <ButtonBase
        disabled={loading}
        onClick={() => handleSendInputText()}
        disableRipple
      >
        <SendIcon color="primary" />
      </ButtonBase>
    </Paper>
  );
}
